
import { useState } from 'react'

interface AvatarDisplayProps {
  avatarId?: string
  username?: string
  email?: string
  size?: 'sm' | 'md' | 'lg' | 'xl'
  showEditButton?: boolean
  onEditClick?: () => void
  className?: string
}

export const AvatarDisplay = ({ 
  avatarId, 
  username, 
  email, 
  size = 'md', 
  showEditButton = false,
  onEditClick,
  className = ''
}: AvatarDisplayProps) => {
  const [imageError, setImageError] = useState(false)

  // Avatar data mapping
  const avatarData: Record<string, { name: string; imageUrl: string }> = {
    'male-casual-1': {
      name: 'Alex',
      imageUrl: 'https://public.readdy.ai/ai/img_res/06f4031f0f5fb5f73ea895ec13b96f2c.jpg'
    },
    'male-casual-2': {
      name: 'Ryan',
      imageUrl: 'https://readdy.ai/api/search-image?query=3D%20metaverse%20style%20male%20avatar%20with%20casual%20t-shirt%20and%20shorts%2C%20sneakers%2C%20relaxed%20pose%2C%20clean%20simple%20background%2C%20digital%20art%20style%2C%20high%20quality%20rendering&width=200&height=200&seq=2&orientation=squarish'
    },
    'male-casual-3': {
      name: 'Jake',
      imageUrl: 'https://readdy.ai/api/search-image?query=3D%20metaverse%20style%20male%20avatar%20with%20casual%20jacket%20and%20pants%2C%20modern%20hairstyle%2C%20confident%20expression%2C%20clean%20simple%20background%2C%20digital%20art%20style%2C%20high%20quality%20rendering&width=200&height=200&seq=3&orientation=squarish'
    },
    'male-professional-1': {
      name: 'Marcus',
      imageUrl: 'https://readdy.ai/api/search-image?query=3D%20metaverse%20style%20male%20avatar%20in%20business%20suit%20and%20tie%2C%20professional%20appearance%2C%20clean%20simple%20background%2C%20digital%20art%20style%2C%20high%20quality%20rendering&width=200&height=200&seq=4&orientation=squarish'
    },
    'male-professional-2': {
      name: 'David',
      imageUrl: 'https://readdy.ai/api/search-image?query=3D%20metaverse%20style%20male%20avatar%20in%20smart%20casual%20blazer%20and%20shirt%2C%20professional%20yet%20approachable%2C%20clean%20simple%20background%2C%20digital%20art%20style%2C%20high%20quality%20rendering&width=200&height=200&seq=5&orientation=squarish'
    },
    'male-creative-1': {
      name: 'Leo',
      imageUrl: 'https://readdy.ai/api/search-image?query=3D%20metaverse%20style%20male%20avatar%20with%20artistic%20clothing%2C%20creative%20accessories%2C%20unique%20hairstyle%2C%20expressive%20pose%2C%20clean%20simple%20background%2C%20digital%20art%20style%2C%20high%20quality%20rendering&width=200&height=200&seq=6&orientation=squarish'
    },
    'male-creative-2': {
      name: 'Zane',
      imageUrl: 'https://readdy.ai/api/search-image?query=3D%20metaverse%20style%20male%20avatar%20with%20trendy%20streetwear%2C%20cap%20and%20graphic%20tee%2C%20modern%20urban%20style%2C%20clean%20simple%20background%2C%20digital%20art%20style%2C%20high%20quality%20rendering&width=200&height=200&seq=7&orientation=squarish'
    },
    'female-casual-1': {
      name: 'Emma',
      imageUrl: 'https://readdy.ai/api/search-image?query=3D%20metaverse%20style%20female%20avatar%20with%20casual%20sweater%20and%20jeans%2C%20friendly%20smile%2C%20long%20hair%2C%20clean%20simple%20background%2C%20digital%20art%20style%2C%20high%20quality%20rendering&width=200&height=200&seq=8&orientation=squarish'
    },
    'female-casual-2': {
      name: 'Sophia',
      imageUrl: 'https://readdy.ai/api/search-image?query=3D%20metaverse%20style%20female%20avatar%20with%20casual%20dress%20and%20sneakers%2C%20relaxed%20pose%2C%20medium%20length%20hair%2C%20clean%20simple%20background%2C%20digital%20art%20style%2C%20high%20quality%20rendering&width=200&height=200&seq=9&orientation=squarish'
    },
    'female-casual-3': {
      name: 'Mia',
      imageUrl: 'https://readdy.ai/api/search-image?query=3D%20metaverse%20style%20female%20avatar%20with%20casual%20top%20and%20skirt%2C%20comfortable%20style%2C%20wavy%20hair%2C%20clean%20simple%20background%2C%20digital%20art%20style%2C%20high%20quality%20rendering&width=200&height=200&seq=10&orientation=squarish'
    },
    'female-professional-1': {
      name: 'Victoria',
      imageUrl: 'https://readdy.ai/api/search-image?query=3D%20metaverse%20style%20female%20avatar%20in%20business%20suit%20and%20blouse%2C%20professional%20appearance%2C%20neat%20hairstyle%2C%20clean%20simple%20background%2C%20digital%20art%20style%2C%20high%20quality%20rendering&width=200&height=200&seq=11&orientation=squarish'
    },
    'female-professional-2': {
      name: 'Grace',
      imageUrl: 'https://readdy.ai/api/search-image?query=3D%20metaverse%20style%20female%20avatar%20in%20smart%20casual%20blazer%20and%20dress%2C%20professional%20yet%20elegant%2C%20clean%20simple%20background%2C%20digital%20art%20style%2C%20high%20quality%20rendering&width=200&height=200&seq=12&orientation=squarish'
    },
    'female-creative-1': {
      name: 'Luna',
      imageUrl: 'https://readdy.ai/api/search-image?query=3D%20metaverse%20style%20female%20avatar%20with%20artistic%20bohemian%20clothing%2C%20creative%20accessories%2C%20unique%20hairstyle%2C%20expressive%20pose%2C%20clean%20simple%20background%2C%20digital%20art%20style%2C%20high%20quality%20rendering&width=200&height=200&seq=13&orientation=squarish'
    },
    'female-creative-2': {
      name: 'Aria',
      imageUrl: 'https://readdy.ai/api/search-image?query=3D%20metaverse%20style%20female%20avatar%20with%20trendy%20streetwear%2C%20fashionable%20outfit%2C%20modern%20hairstyle%2C%20confident%20pose%2C%20clean%20simple%20background%2C%20digital%20art%20style%2C%20high%20quality%20rendering&width=200&height=200&seq=14&orientation=squarish'
    }
  }

  const sizeClasses = {
    sm: 'w-8 h-8',
    md: 'w-12 h-12',
    lg: 'w-16 h-16',
    xl: 'w-24 h-24'
  }

  const textSizeClasses = {
    sm: 'text-xs',
    md: 'text-sm',
    lg: 'text-lg',
    xl: 'text-2xl'
  }

  const avatar = avatarId ? avatarData[avatarId] : null
  const fallbackInitial = username?.charAt(0).toUpperCase() || email?.charAt(0).toUpperCase() || '?'

  return (
    <div className={`relative ${className}`}>
      <div className={`${sizeClasses[size]} rounded-full overflow-hidden bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center relative`}>
        {avatar && !imageError ? (
          <img
            src={avatar.imageUrl}
            alt={avatar.name}
            className="w-full h-full object-cover object-top"
            onError={() => setImageError(true)}
            loading="lazy"
          />
        ) : (
          <span className={`text-white font-bold ${textSizeClasses[size]}`}>
            {fallbackInitial}
          </span>
        )}
      </div>

      {showEditButton && onEditClick && (
        <button
          onClick={onEditClick}
          className="absolute -bottom-1 -right-1 w-6 h-6 bg-purple-600 rounded-full flex items-center justify-center text-white hover:bg-purple-700 transition-colors cursor-pointer"
          title="Change avatar"
        >
          <i className="ri-edit-line text-xs"></i>
        </button>
      )}
    </div>
  )
}

export default AvatarDisplay
